"use client";

/**
 * AppPlayLaunchProvider — the shell's Play launch seam.
 *
 * Wires `PlayLaunchProvider` from @excellent/ui with a `launch` function that
 * dispatches the `excellent:open-stage` custom event, so the Play button, the
 * ⌘⏎ shortcut, and the URL deep-link all call the SAME code path.
 *
 * On mount, reads `?play=1` (+ optional `?start=<actionKey>`) from search params
 * and, if present, fires the launch then strips the query params — so a reload
 * won't re-fire. This is the deep-link seam an external agent uses to kick the
 * user into the Play question flow without JS injection.
 *
 * Zero migrations; client-only.
 */

import { useEffect, useCallback, type ReactNode } from "react";
import { useNavigate, useLocation } from "@tanstack/react-router";
import { PlayLaunchProvider, type PlayLaunchOptions } from "@excellent/ui";
import { OPEN_STAGE_EVENT, type OpenStageDetail } from "./stage-context";

export function AppPlayLaunchProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  // ── launch: dispatch the same custom event the Play button / shortcut fires ──
  const launch = useCallback((opts?: PlayLaunchOptions) => {
    const detail: OpenStageDetail = {
      kind: "play",
      slug: "play",
      name: "Play",
      startActionKey: opts?.startActionKey,
    };
    window.dispatchEvent(new CustomEvent(OPEN_STAGE_EVENT, { detail }));
  }, []);

  // ── deep-link trigger: ?play=1[&start=<key>] ──────────────────────────────
  useEffect(() => {
    const searchParams = new URLSearchParams(location.searchStr);
    const playVal = searchParams.get("play");
    if (playVal !== "1") return;

    const startKey = searchParams.get("start") ?? undefined;
    launch({ startActionKey: startKey });

    // Strip the params so a refresh doesn't re-fire.
    const params = new URLSearchParams(location.searchStr);
    params.delete("play");
    params.delete("start");
    const qs = params.toString();
    void navigate({
      to: location.pathname,
      search: qs ? Object.fromEntries(new URLSearchParams(qs)) : undefined,
      replace: true,
    });
  }, [location.searchStr, location.pathname, navigate, launch]);

  return (
    <PlayLaunchProvider launch={launch}>
      {children}
    </PlayLaunchProvider>
  );
}
